/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * useAudioDevices.ts — localized browser audio-device discovery and selection.
 * The hook obtains permission, enumerates microphones and speakers, persists
 * operator preferences, and exposes constraints for voice transports. It does
 * not create media sessions or transmit audio.
 */

/**
 * E3-S2: Enumerate audio input/output devices and persist user choice.
 * Used for WebRTC and future mic capture; constraints can be passed to getUserMedia.
 */
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '@/i18n';

const STORAGE_INPUT = 'operator.audio.inputDeviceId';
const STORAGE_OUTPUT = 'operator.audio.outputDeviceId';

export interface AudioDeviceInfo {
  deviceId: string;
  kind: 'audioinput' | 'audiooutput';
  label: string;
  groupId: string;
}

export interface AudioDevicesState {
  inputs: AudioDeviceInfo[];
  outputs: AudioDeviceInfo[];
  preferredInputId: string | null;
  preferredOutputId: string | null;
  error: string | null;
  permission: 'prompt' | 'granted' | 'denied' | null;
}

function getStoredInputId(): string | null {
  try {
    return localStorage.getItem(STORAGE_INPUT);
  } catch {
    return null;
  }
}

function getStoredOutputId(): string | null {
  try {
    return localStorage.getItem(STORAGE_OUTPUT);
  } catch {
    return null;
  }
}

function setStoredInputId(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_INPUT, id);
    else localStorage.removeItem(STORAGE_INPUT);
  } catch {
    /* ignore */
  }
}

function setStoredOutputId(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_OUTPUT, id);
    else localStorage.removeItem(STORAGE_OUTPUT);
  } catch {
    /* ignore */
  }
}

async function enumerateDevices(labels: { microphone: string; speakers: string }): Promise<{
  inputs: AudioDeviceInfo[];
  outputs: AudioDeviceInfo[];
}> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const inputs: AudioDeviceInfo[] = [];
  const outputs: AudioDeviceInfo[] = [];
  for (const d of devices) {
    if (d.kind === 'audioinput') {
      inputs.push({
        deviceId: d.deviceId,
        kind: 'audioinput',
        label: d.label || `${labels.microphone} ${inputs.length + 1}`,
        groupId: d.groupId,
      });
    } else if (d.kind === 'audiooutput') {
      outputs.push({
        deviceId: d.deviceId,
        kind: 'audiooutput',
        label: d.label || `${labels.speakers} ${outputs.length + 1}`,
        groupId: d.groupId,
      });
    }
  }
  return { inputs, outputs };
}

/**
 * Returns MediaTrackConstraints for getUserMedia from preferred input id.
 */
export function getPreferredInputConstraints(): MediaTrackConstraints | undefined {
  const id = getStoredInputId();
  if (!id) return undefined;
  return { deviceId: id };
}

/**
 * Returns constraint for setSinkId (output) from preferred output id.
 */
export function getPreferredOutputId(): string | null {
  return getStoredOutputId();
}

export function useAudioDevices(): AudioDevicesState & {
  refresh: () => Promise<void>;
  setPreferredInput: (deviceId: string | null) => void;
  setPreferredOutput: (deviceId: string | null) => void;
  requestPermissionAndRefresh: () => Promise<void>;
} {
  const t = useTranslation();
  const [inputs, setInputs] = useState<AudioDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<AudioDeviceInfo[]>([]);
  const [preferredInputId, setPreferredInputIdState] = useState<string | null>(getStoredInputId);
  const [preferredOutputId, setPreferredOutputIdState] = useState<string | null>(getStoredOutputId);
  const [error, setError] = useState<string | null>(null);
  const [permission, setPermission] = useState<'prompt' | 'granted' | 'denied' | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.enumerateDevices) {
      setError(t('audio.enumeration_unsupported'));
      return;
    }
    try {
      const { inputs: i, outputs: o } = await enumerateDevices({
        microphone: t('audio.microphone'),
        speakers: t('audio.speakers'),
      });
      setInputs(i);
      setOutputs(o);
      setPermission(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('audio.enumeration_failed'));
    }
  }, [t]);

  const requestPermissionAndRefresh = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setPermission('granted');
      await refresh();
    } catch (e) {
      setPermission('denied');
      setError(e instanceof Error ? e.message : t('audio.microphone_denied'));
    }
  }, [refresh, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setPreferredInput = useCallback((deviceId: string | null) => {
    setPreferredInputIdState(deviceId);
    setStoredInputId(deviceId);
  }, []);

  const setPreferredOutput = useCallback((deviceId: string | null) => {
    setPreferredOutputIdState(deviceId);
    setStoredOutputId(deviceId);
  }, []);

  return {
    inputs,
    outputs,
    preferredInputId,
    preferredOutputId,
    error,
    permission,
    refresh,
    setPreferredInput,
    setPreferredOutput,
    requestPermissionAndRefresh,
  };
}
